import Modal from "@/common/components/Modal";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { FormTransformers } from "@/common/utils/formTransformers";
import MaintenanceSpecificationForm from "@/features/establishmentLeadsFunnel/components/maintenanceModule/maintenanceSpecification/MaintenanceSpecificationForm";
import { useUpdateEstablishmentCompanyMaintenanceSpecification } from "@/features/establishments/hooks/maintenanceModels/useUpdateEstablishmentCompanyMaintenanceSpecification";
import { maintenanceSpecificationSchema, MaintenanceSpecificationValidationSchema } from "@/features/establishments/schemas/maintenanceSpecification.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

interface UpdateMaintenanceSpecificationModalProps {
  establishmentCompany: EstablishmentCompanyData;
  maintenanceSpecification: EstablishmentCompanyData['maintenance_specifications'][number];
  alertText: {
    title: string;
    description: string;
  };
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const UpdateMaintenanceSpecificationModal = ({
  establishmentCompany,
  maintenanceSpecification,
  alertText,
  isOpen,
  setIsOpen,
}: UpdateMaintenanceSpecificationModalProps) => {
  const queryClient = useQueryClient();

  const formMethods = useForm<MaintenanceSpecificationValidationSchema>({
    resolver: zodResolver(maintenanceSpecificationSchema),
  });

  const {
    setError,
    handleSubmit,
    reset,
  } = formMethods;

  const {
    mutate: updateMaintenanceSpecification,
    isPending: updateMaintenanceSpecificationIsPending,
  } = useUpdateEstablishmentCompanyMaintenanceSpecification({
    establishmentCompanyId: establishmentCompany.id,
    maintenanceSpecification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany.id] });

      setIsOpen(false);
    },
    onError: FormTransformers.errorsTransformer<MaintenanceSpecificationValidationSchema>({
      setError,
    }),
  });

  const onSubmit: SubmitHandler<MaintenanceSpecificationValidationSchema> = (data) => updateMaintenanceSpecification(data);

  useEffect(() => {
    if (isOpen) {
      reset({
        equipment_category_specifications_attributes: maintenanceSpecification.equipment_category_specifications.map(
          ({ category }) => ({
            value: category.id,
            label: category.display_name,
          }),
        ),
        equipment_manufacturer_specifications_attributes: maintenanceSpecification.equipment_manufacturer_specifications.map(
          ({ manufacturer }) => ({
            value: manufacturer.id,
            label: manufacturer.display_name,
          }),
        ),
        system_specifications_attributes: maintenanceSpecification.system_specifications.map(
          ({ system }) => ({
            value: system.id,
            label: system.display_name,
          }),
        ),
      })
    }
  }, [isOpen]);

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title="Especificando atendimentos"
        useCancellableFooter
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={updateMaintenanceSpecificationIsPending}
        body={(
          <FormProvider {...formMethods}>
            <MaintenanceSpecificationForm
              alertText={alertText}
            />
          </FormProvider>
        )}
      />
    </div>
  );
}

export default UpdateMaintenanceSpecificationModal;
